import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type {
  DocumentServicesObject,
  CompRef,
} from '@wix/document-services-types';
import type {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
  TPAMessageDataAny,
} from '../../types/tpaMessage';

export function createWindowPlacementHandlers(
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
) {
  const getWindowPlacement = async (
    _msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const [selectedComp] =
      await platformApiMethods.editor.selection.getSelectedComponents();

    if (!selectedComp) {
      throw new Error('Component should be selected for getWindowPlacement');
    }

    const compProps =
      documentServicesAPI.components.properties.get(selectedComp);

    if (compProps) {
      callback({
        placement: compProps.placement,
        verticalMargin: parseFloat(compProps.verticalMargin || '0'),
        horizontalMargin: parseFloat(compProps.horizontalMargin || '0'),
      });
    } else {
      throw new Error('Selected widget is not Glued Widget');
    }
  };

  const setWindowPlacement: TPAHandler = (msg: TPAMessage) => {
    const data = msg?.data as { placement: string } & TPAMessageDataAny;
    const { placement } = data;

    if (placement === 'CENTER') {
      throw new Error(
        `Invalid position: ${placement}. please use a valid position`,
      );
    }

    const verticalMargin = data.verticalMargin || 0;
    const horizontalMargin = data.horizontalMargin || 0;
    const compRef = documentServicesAPI.components.get.byId(
      msg.compId,
      'masterPage',
    ) as CompRef;

    const compProps = documentServicesAPI.components.properties.get(compRef);

    Object.assign(compProps, {
      placement,
      verticalMargin,
      horizontalMargin,
    });

    documentServicesAPI.components.properties.update(compRef, compProps);

    if (documentServicesAPI.tpa.change.trigger.windowPlacement) {
      if (msg.data && typeof msg.data.placement === 'string') {
        documentServicesAPI.tpa.change.trigger.windowPlacement({
          data: { placement: msg.data.placement },
          compId: msg.compId,
        });
      } else {
        throw new Error('Invalid message data for windowPlacement');
      }
    }
  };

  return {
    getWindowPlacement,
    setWindowPlacement,
  };
}
